import { type Api, type WorkoutSetType } from "api-client";
import { type ExerciseAttributeType } from "api-client/legacy-models";
import {
  type WorkoutFormat,
  type WorkoutItemGroupType,
  type WorkoutType,
} from "api-client/legacy-trpc";
import { sort } from "fast-sort";
import { computed, makeAutoObservable, runInAction } from "mobx";
import {
  assert,
  orderUtilsMutable,
  mapBy,
  type ExerciseThumbnail,
  type SportType,
} from "utils";

import { findMainCardioItem } from "./findMainCardioItem";
import { getVisibleWorkoutItemAttributes } from "./getVisibleWorkoutItemAttributes";

export interface WorkoutData {
  id: string;
  name: string;
  description: string;
  note: string | null;
  isInLibrary: boolean;
  workoutType: WorkoutType;
  isCardio: boolean;
  image?: { id: string; key: string; url: string } | null;
  workoutItems: (RootWorkoutItemData | CardioWorkoutItemData)[];
}

interface WorkoutItemAttributeData {
  workoutItemId: string;
  exerciseAttributeId: string;
  order: number;
  deletedAt: string | Date | null;
  exerciseAttribute: {
    id: string;
    name: string;
    type: ExerciseAttributeType;
  };
}

interface BaseWorkoutItemData {
  id: string;
  order: number;
  parentId: string | null;
  instructions: string | null;
  tempo: string | null;
  workoutSets: WorkoutSetData[];
  workoutItemAttributes: WorkoutItemAttributeData[];
  exercise: ExerciseData | null;
}

interface RootBaseWorkoutItemData extends BaseWorkoutItemData {
  name: string;
  baseWorkoutItemId: string | null;
  commentGroupId: string | null;
  selectedChildId: string | null;
}

export interface NormalWorkoutItemData extends RootBaseWorkoutItemData {
  type: "NORMAL";
  eachSide?: boolean | null;
  alternateWorkoutItems: AlternateWorkoutItemData[];
}

export interface GroupWorkoutItemData extends RootBaseWorkoutItemData {
  type: "GROUP";
  groupType: WorkoutItemGroupType | null;
  format: WorkoutFormat | null;
  durationMinutes: number | null;
  rounds: number | null;
  mediaUrl: string | null;
  media: { url: string; key: string } | null;
}

export interface AlternateWorkoutItemData extends BaseWorkoutItemData {
  type: "ALTERNATE";
  parentId: string;
  eachSide?: boolean | null;
}

export interface CardioSectionData {
  id: string;
  order: number;
  name: string | null;
}

export interface CardioAlternateWorkoutItemData {
  id: string;
  order: number;
  type: "ALTERNATE";
  parentId: string;
  instructions: string | null;
  dslText: string | null;
  workoutType: WorkoutType;
  sportType: SportType;
  sections: CardioSectionData[];
}

export interface CardioContentData {
  instructions: string | null;
  dslText: string | null;
  workoutType: WorkoutType;
  sportType: SportType;
}

// Main cardio content of a cardio workout; alternates are other cardio options.
export interface CardioWorkoutItemData extends RootBaseWorkoutItemData {
  type: "CARDIO";
  instructions: string | null;
  dslText: string | null;
  workoutType: WorkoutType;
  sportType: SportType;
  sections: CardioSectionData[];
  alternateWorkoutItems: CardioAlternateWorkoutItemData[];
}

export type RootWorkoutItemData = NormalWorkoutItemData | GroupWorkoutItemData;

export type WorkoutItemData =
  | NormalWorkoutItemData
  | GroupWorkoutItemData
  | AlternateWorkoutItemData;

export interface WorkoutSetData {
  id: string;
  order: number;
  round?: number | null;
  type?: WorkoutSetType | null;
  completed: boolean;
  workoutItemId: string;
  attributeValues: AttributeValueData[];
}

export interface AttributeValueData {
  id: string;
  assignedValue: string;
  trackedValue: string;
  attribute: {
    id: string;
    name: string;
    type: ExerciseAttributeType;
  };
}

export interface ExerciseData {
  id: string;
  name: string;
  videoURL: string;
  images: { url: string }[];
  previewImages: { url: string }[];
  uploadedVideo: { url: string } | null;
  thumbnail: ExerciseThumbnail;
  exerciseAttributes: {
    exerciseAttributeId: string;
    exerciseId: string;
    order: number;
    exerciseAttribute: {
      id: string;
      name: string;
      type: ExerciseAttributeType;
    };
  }[];
}
export type WorkoutItemLayout = {
  workoutItemId: string;
  type: "NORMAL" | "GROUP" | "ALTERNATE";
  children?: WorkoutItemLayout[];
};

type WorkoutLayout = {
  workoutId: string;
  items: WorkoutItemLayout[];
};

// Alternates are stored nested, but order helpers walk them with root items.
type StoredWorkoutItemData =
  | WorkoutData["workoutItems"][number]
  | WorkoutItemData;

function isStrengthItem(
  item: WorkoutData["workoutItems"][number],
): item is RootWorkoutItemData {
  return item.type !== "CARDIO";
}

export class WorkoutEditStore {
  optimisticAttributeValues: Record<
    string,
    Record<string, AttributeValueData>
  > = {};

  selectedAlternates: Record<string, string> = {};

  workoutData: WorkoutData;
  _draftItemIds: Map<string, boolean>;

  constructor(workoutData: WorkoutData) {
    this.workoutData = workoutData;
    this._draftItemIds = new Map();
    this.workoutData.workoutItems.forEach((item) => {
      if (item.selectedChildId) {
        this.selectedAlternates[item.id] = item.selectedChildId;
      }
    });
    makeAutoObservable(this);
  }

  get workout() {
    return this.workoutData;
  }

  get workoutItems(): WorkoutItemData[] {
    const rootItems = this.workoutData.workoutItems.filter(isStrengthItem);

    const alternates = rootItems.flatMap((item) => {
      if ("alternateWorkoutItems" in item && item.alternateWorkoutItems) {
        return item.alternateWorkoutItems;
      }

      return [];
    });

    return [...rootItems, ...alternates];
  }

  get workoutItemById() {
    const result = mapBy(this.workoutItems, (x) => {
      return x.id;
    });
    return result;
  }

  get exercises() {
    return this.workoutItems
      .map((x) => {
        return x.exercise;
      })
      .filter(Boolean);
  }

  get exerciseAttributesById() {
    const exerciseAttributes = this.exercises
      .flatMap((exercise) => {
        return exercise.exerciseAttributes;
      })
      .map((exerciseAttribute) => {
        return exerciseAttribute.exerciseAttribute;
      });

    const workoutItemAttributes = this.workoutItems.flatMap((workoutItem) => {
      return workoutItem.workoutItemAttributes.map((attribute) => {
        return attribute.exerciseAttribute;
      });
    });

    return mapBy([...exerciseAttributes, ...workoutItemAttributes], (x) => {
      return x.id;
    });
  }

  get exerciseById() {
    return mapBy(this.exercises, (x) => x.id);
  }

  get rootWorkoutItems(): RootWorkoutItemData[] {
    return this.workoutItems
      .filter((workoutItem) => {
        return workoutItem.type === "NORMAL" || workoutItem.type === "GROUP";
      })
      .filter((wi) => wi.parentId === null);
  }

  getNextOrder(parentId: string | null): number {
    const items =
      parentId === null ? this.rootWorkoutItems : this.getChildrenOf(parentId);

    if (items.length === 0) {
      return 0;
    }

    const max = Math.max(
      ...items.map((item) => {
        return item.order;
      }),
    );

    return max + 1;
  }

  get exercisesById() {
    return mapBy(this.exercises, (e) => {
      return e.id;
    });
  }

  get allWorkoutSets(): WorkoutSetData[] {
    return this.workoutItems.flatMap((workoutItem) => {
      return workoutItem.workoutSets;
    });
  }

  get workoutSetsById() {
    return mapBy(this.allWorkoutSets, (x) => {
      return x.id;
    });
  }

  getSetById(setId: string): WorkoutSetData {
    const set = this.workoutSetsById[setId];
    assert(set, "set does not exist");
    return set;
  }

  toggleCompleteSet(setId: string) {
    const set = this.workoutSetsById[setId];
    set.completed = !set.completed;

    if (set.completed) {
      for (const attributeValue of set.attributeValues) {
        if (attributeValue.trackedValue === "") {
          attributeValue.trackedValue = attributeValue.assignedValue;
        }
      }
    }
  }

  get attributeValuesBySetAndAttribute() {
    const result: Record<string, Record<string, AttributeValueData>> = {};

    for (const workoutSet of this.allWorkoutSets) {
      result[workoutSet.id] = {};

      for (const attributeValue of workoutSet.attributeValues) {
        result[workoutSet.id][attributeValue.attribute.id] = attributeValue;
      }
    }

    for (const setId in this.optimisticAttributeValues) {
      if (!result[setId]) {
        result[setId] = {};
      }

      Object.assign(result[setId], this.optimisticAttributeValues[setId]);
    }

    return result;
  }

  get childWorkoutItems() {
    return this.workoutItems.filter(
      (
        workoutItem,
      ): workoutItem is WorkoutItemData & {
        parentId: string;
      } => {
        return workoutItem.parentId !== null;
      },
    );
  }

  get workoutItemsByParentId() {
    const byParentId: Record<string, WorkoutItemData[]> = {};

    for (const childWorkoutItem of this.childWorkoutItems) {
      let existing = byParentId[childWorkoutItem.parentId];

      if (!existing) {
        existing = [];
        byParentId[childWorkoutItem.parentId] = existing;
      }

      existing.push(childWorkoutItem);
    }

    return byParentId;
  }

  get layout(): WorkoutLayout {
    const buildLayoutItem = (item: WorkoutItemData): WorkoutItemLayout => {
      const childrenRaw = this.workoutItemsByParentId[item.id];
      const sortedChildren = childrenRaw
        ? sort(childrenRaw).asc((x) => {
            return "order" in x && x.order;
          })
        : undefined;

      const children: WorkoutItemLayout[] | undefined = sortedChildren?.map(
        (childItem) => {
          return buildLayoutItem(childItem);
        },
      );

      return {
        workoutItemId: item.id,
        children: children,
        type: item.type,
      };
    };

    const sortedRootItems = sort(this.rootWorkoutItems).asc((x) => {
      return x.order;
    });

    const items = sortedRootItems.map((item) => {
      return buildLayoutItem(item);
    });

    return {
      workoutId: this.workout.id,
      items: items,
    };
  }

  getWorkoutItemById(id: string): WorkoutItemData {
    return this.workoutItemById[id];
  }

  getIsWorkoutItemSuperset(workoutItemId: string) {
    const workoutItem = this.getWorkoutItemById(workoutItemId);
    return (
      workoutItem.type === "GROUP" && workoutItem.groupType === "SUPER_SET"
    );
  }

  getGroupWorkoutItemById(id: string): GroupWorkoutItemData {
    const workoutItem = this.getWorkoutItemById(id);
    assert(workoutItem.type === "GROUP", "Group workout item not found");
    return workoutItem;
  }

  isDraft(workoutItemId: string): boolean {
    return this._draftItemIds.has(workoutItemId);
  }

  markItemAsDraft(workoutItemId: string) {
    this._draftItemIds.set(workoutItemId, true);
  }

  updateWorkout(
    updates: Partial<{
      name: string;
      description: string;
    }>,
  ) {
    Object.assign(this.workoutData, updates);
  }

  get mainCardioItem(): CardioWorkoutItemData {
    const item = findMainCardioItem(this.workoutData.workoutItems);
    assert(item, "Main cardio item not found");
    return item;
  }

  get cardioAlternates(): CardioAlternateWorkoutItemData[] {
    return sort(this.mainCardioItem.alternateWorkoutItems).asc((alternate) => {
      return alternate.order;
    });
  }

  // The workout's sport is its main cardio item's sport.
  updateMainCardioItem(update: CardioContentData) {
    Object.assign(this.mainCardioItem, update);
    this.workoutData.workoutType = update.workoutType;
  }

  updateMainCardioItemSections(sections: CardioSectionData[]) {
    this.mainCardioItem.sections = sections;
  }

  // After make-main the alternate is the main and the old main its alternate.
  replaceMainCardioItem(item: CardioWorkoutItemData) {
    this.replaceWorkoutItem(this.mainCardioItem.id, item);
    this.workoutData.workoutType = item.workoutType;
  }

  replaceWorkoutItem(
    workoutItemId: string,
    item: WorkoutData["workoutItems"][number],
  ) {
    const index = this.workoutData.workoutItems.findIndex((workoutItem) => {
      return workoutItem.id === workoutItemId;
    });
    assert(index >= 0, "Workout item not found");
    this.workoutData.workoutItems[index] = item;
    delete this.selectedAlternates[workoutItemId];
  }

  addCardioAlternate(alternate: CardioAlternateWorkoutItemData) {
    this.mainCardioItem.alternateWorkoutItems.push(alternate);
  }

  updateCardioAlternate(alternateId: string, update: CardioContentData) {
    const alternate = this.cardioAlternates.find((item) => {
      return item.id === alternateId;
    });
    assert(alternate, "Cardio alternate not found");
    Object.assign(alternate, update);
  }

  removeCardioAlternate(alternateId: string) {
    const main = this.mainCardioItem;
    main.alternateWorkoutItems = main.alternateWorkoutItems.filter(
      (alternate) => {
        return alternate.id !== alternateId;
      },
    );
  }

  reorderCardioAlternates(alternateIds: string[]) {
    for (const alternate of this.cardioAlternates) {
      alternate.order = alternateIds.indexOf(alternate.id);
    }
  }

  updateWorkoutImage(image: { id: string; key: string; url: string } | null) {
    this.workoutData.image = image;
  }

  updateWorkoutItem(id: string, update: Partial<WorkoutItemData>) {
    const workoutItem = this.getWorkoutItemById(id);

    if (!workoutItem) {
      return;
    }

    Object.assign(workoutItem, update);
  }

  updateWorkoutItemAttributes(
    update: Api.UpdateWorkoutItemAttributes.Output.Self,
  ) {
    const workoutItem = this.getWorkoutItemById(update.workoutItemId);

    if (!workoutItem) {
      return;
    }

    workoutItem.workoutItemAttributes = update.workoutItemAttributes;
    workoutItem.workoutSets = update.workoutSets;
  }

  updateItems(workoutItems: WorkoutData["workoutItems"]) {
    this.workoutData.workoutItems = workoutItems;
  }

  addWorkoutSet(workoutSet: WorkoutSetData) {
    const workoutItem = this.workoutItemById[workoutSet.workoutItemId];

    if (workoutItem) {
      workoutItem.workoutSets = [...workoutItem.workoutSets, workoutSet];
    }
  }

  addWorkoutSets(workoutSets: WorkoutSetData[]) {
    for (const workoutSet of workoutSets) {
      this.addWorkoutSet(workoutSet);
    }
  }

  getWorkoutItemAttributes(workoutItemId: string) {
    const workoutItem = this.getWorkoutItemById(workoutItemId);

    return getVisibleWorkoutItemAttributes(
      workoutItem.workoutItemAttributes,
    ).map((attribute) => {
      return {
        exerciseAttributeId: attribute.exerciseAttributeId,
        exerciseId: workoutItem.exercise?.id ?? "",
        order: attribute.order,
        exerciseAttribute: attribute.exerciseAttribute,
      };
    });
  }

  getWorkoutItemSets(workoutItemId: string): WorkoutSetData[] {
    const workoutItem = this.getWorkoutItemById(workoutItemId);
    return workoutItem?.workoutSets ?? [];
  }

  getWorkoutItemExercise(workoutItemId: string): ExerciseData | null {
    const workoutItem = this.getWorkoutItemById(workoutItemId);
    return workoutItem?.exercise ?? null;
  }

  updateAttributeValue(
    workoutSetId: string,
    exerciseAttributeId: string,
    update: Partial<AttributeValueData>,
  ) {
    const attributeValue =
      this.attributeValuesBySetAndAttribute[workoutSetId]?.[
        exerciseAttributeId
      ];

    if (!attributeValue) {
      this.optimisticAttributeValues[workoutSetId] =
        this.optimisticAttributeValues[workoutSetId] || {};

      this.optimisticAttributeValues[workoutSetId][exerciseAttributeId] = {
        id: `optimistic-${workoutSetId}-${exerciseAttributeId}`,
        attribute: {
          id: exerciseAttributeId,
          name: this.exerciseAttributesById[exerciseAttributeId].name,
          type: this.exerciseAttributesById[exerciseAttributeId].type,
        },
        trackedValue: "",
        assignedValue: "",
        ...update,
      };
      return;
    }

    Object.assign(attributeValue, update);
  }

  logSetProgressValue(
    workoutSetId: string,
    exerciseAttributeId: string,
    trackedValue: string,
  ) {
    this.updateAttributeValueForCurrentAndFollowingSets(
      workoutSetId,
      exerciseAttributeId,
      {
        trackedValue: trackedValue,
      },
    );
  }

  assignSetAssignedValues(
    workoutSetId: string,
    exerciseAttributeId: string,
    assignedValue: string,
  ) {
    this.updateAttributeValueForCurrentAndFollowingSets(
      workoutSetId,
      exerciseAttributeId,
      {
        assignedValue: assignedValue,
      },
    );
  }

  updateAttributeValueForCurrentAndFollowingSets(
    workoutSetId: string,
    exerciseAttributeId: string,
    update: Partial<AttributeValueData>,
  ) {
    const workoutSet = this.workoutSetsById[workoutSetId];

    if (!workoutSet) {
      return;
    }

    const workoutSets = sort(
      this.getWorkoutItemSets(workoutSet.workoutItemId),
    ).asc((set) => {
      return set.order;
    });

    const workoutSetIndex = workoutSets.findIndex((set) => {
      return set.id === workoutSetId;
    });

    if (workoutSetIndex === -1) {
      return;
    }

    const followingSets = workoutSets.slice(workoutSetIndex);

    for (const followingSet of followingSets) {
      this.updateAttributeValue(followingSet.id, exerciseAttributeId, update);
    }
  }

  updateWorkoutSet(workoutSetId: string, update: Partial<WorkoutSetData>) {
    Object.assign(this.workoutSetsById[workoutSetId], update);
  }

  removeWorkoutSet(workoutSetId: string) {
    const workoutSet = this.workoutSetsById[workoutSetId];
    const workoutItem = this.workoutItemById[workoutSet.workoutItemId];

    if (workoutItem) {
      workoutItem.workoutSets = workoutItem.workoutSets.filter(
        (x) => x.id !== workoutSetId,
      );
    }
  }

  removeItem(id: string) {
    const item = this.workoutItemById[id];

    if (!item) return;

    const group =
      item.parentId === null
        ? this.rootWorkoutItems
        : (this.workoutItemsByParentId[item.parentId] ?? []);

    runInAction(() => {
      orderUtilsMutable.remove<StoredWorkoutItemData>(
        this.workoutData.workoutItems,
        item,
        group,
      );
    });
    this._draftItemIds.delete(id);
  }

  insertWorkoutItem(at: number, item: WorkoutItemData) {
    const group =
      item.parentId === null
        ? this.rootWorkoutItems
        : (this.workoutItemsByParentId[item.parentId] ?? []);

    runInAction(() => {
      orderUtilsMutable.insertAtOrder<StoredWorkoutItemData>(
        this.workoutData.workoutItems,
        item,
        at,
        group,
      );
    });
  }

  createDraftItem(
    at: number,
    parentId: string | null,
    type: "NORMAL" | "GROUP",
  ): WorkoutItemData {
    const draftId = `draft-${Date.now()}-${Math.random()}`;

    function getDraft(): RootWorkoutItemData {
      const baseDraft = {
        id: draftId,
        name: "",
        order: at,
        parentId: parentId,
        baseWorkoutItemId: null,
        commentGroupId: null,
        workoutSets: [],
        workoutItemAttributes: [],
        exercise: null,
        instructions: null,
        tempo: null,
        selectedChildId: null,
      };

      if (type === "NORMAL") {
        return {
          ...baseDraft,
          type: "NORMAL",
          alternateWorkoutItems: [],
        };
      } else {
        return {
          ...baseDraft,
          type: "GROUP",
          durationMinutes: null,
          format: null,
          groupType: null,
          media: null,
          mediaUrl: null,
          rounds: null,
        };
      }
    }

    const draftItem = getDraft();
    this.insertWorkoutItem(at, draftItem);
    this.markItemAsDraft(draftId);

    return draftItem;
  }

  getNormalWorkoutItemById(workoutItemId: string): NormalWorkoutItemData {
    const workoutItem = this.workoutItemById[workoutItemId];
    assert(workoutItem.type === "NORMAL", "Normal Workout item not found");
    return workoutItem;
  }

  addWorkoutItemAlternate(
    workoutItemId: string,
    alternate: AlternateWorkoutItemData,
  ) {
    const normalItem = this.getNormalWorkoutItemById(workoutItemId);
    const alternates = normalItem.alternateWorkoutItems ?? [];

    normalItem.alternateWorkoutItems = [...alternates, alternate];
  }

  removeWorkoutItemAlternate(workoutItemId: string, alternateId: string) {
    const normalItem = this.getNormalWorkoutItemById(workoutItemId);

    if (this.selectedAlternates[workoutItemId] === alternateId) {
      delete this.selectedAlternates[workoutItemId];
    }

    normalItem.alternateWorkoutItems = normalItem.alternateWorkoutItems = (
      normalItem.alternateWorkoutItems || []
    ).filter((alt) => {
      return alt.id !== alternateId;
    });
  }

  getActiveWorkoutItemId(workoutItemId: string): string {
    return computed(() => {
      return this.selectedAlternates[workoutItemId] ?? workoutItemId;
    }).get();
  }

  getAlternatesForWorkoutItem(
    workoutItemId: string,
  ): AlternateWorkoutItemData[] {
    const normalItem = this.getNormalWorkoutItemById(workoutItemId);
    return sort(normalItem.alternateWorkoutItems ?? []).asc((x) => [
      x.order,
      x.exercise?.name,
    ]);
  }

  reorderWorkoutItemAlternates(workoutItemId: string, alternateIds: string[]) {
    for (const alternate of this.getAlternatesForWorkoutItem(workoutItemId)) {
      alternate.order = alternateIds.indexOf(alternate.id);
    }
  }

  selectAlternate(
    workoutItemId: string,
    alternateWorkoutItemId: string | null,
  ) {
    if (alternateWorkoutItemId === null) {
      delete this.selectedAlternates[workoutItemId];
    } else {
      this.selectedAlternates[workoutItemId] = alternateWorkoutItemId;
    }
  }

  getWorkoutItemCommentExerciseIds(workoutItemId: string): string[] {
    const workoutItem = this.workoutItemById[workoutItemId];
    const alternates =
      workoutItem.type === "NORMAL" ? workoutItem.alternateWorkoutItems : [];

    return [workoutItem, ...alternates].flatMap((item) => {
      return item.exercise ? [item.exercise.id] : [];
    });
  }

  getActiveExerciseId(workoutItemId: string): string {
    const activeWorkoutItem =
      this.workoutItemById[this.getActiveWorkoutItemId(workoutItemId)];
    assert(activeWorkoutItem.exercise, "Workout item has no exercise");
    return activeWorkoutItem.exercise.id;
  }

  getCommentsWorkoutItemId(target: string): string | null {
    if (this.workoutItemById[target]) {
      return target;
    }

    const workoutItem = this.workoutItems.find((item) => {
      return (
        item.type === "NORMAL" &&
        this.getWorkoutItemCommentExerciseIds(item.id).includes(target)
      );
    });

    return workoutItem?.id ?? null;
  }

  getWorkoutItemName(workoutItemId: string): string {
    const workoutItem = this.workoutItemById[workoutItemId];

    if ("name" in workoutItem) {
      return workoutItem.name;
    }

    return "";
  }

  getBaseWorkoutItemId(workoutItemId: string): string | null {
    const workoutItem = this.workoutItemById[workoutItemId];

    if ("baseWorkoutItemId" in workoutItem) {
      return workoutItem.baseWorkoutItemId;
    }

    return "";
  }

  getWorkoutSetById(setId: string): WorkoutSetData {
    return this.workoutSetsById[setId];
  }

  applyWorkoutItemChanges(
    operations:
      | Api.LinkWorkoutItems.Output.Self["operations"]
      | Api.UnlinkWorkoutItem.Output.Self["operations"]
      | Api.ImportSection.Output.Self["operations"],
  ) {
    runInAction(() => {
      for (const change of operations) {
        if (change.operation === "delete") {
          this.workoutData.workoutItems = this.workoutData.workoutItems.filter(
            (item) => {
              return item.id !== change.data;
            },
          );
        } else if (change.operation === "update") {
          const existingItem = this.workoutData.workoutItems.find((item) => {
            return item.id === change.data.id;
          });

          if (existingItem) {
            Object.assign(existingItem, change.data);
          }
        } else if (change.operation === "create") {
          this.workoutData.workoutItems.push(change.data);
        }
      }
    });
  }

  canMergeWithNext(itemId: string): boolean {
    const item = this.workoutItemById[itemId];

    if (!item) {
      return false;
    }

    if (item.type === "GROUP" && "groupType" in item) {
      const groupType = item.groupType;

      if (groupType !== null && groupType !== "SUPER_SET") {
        return false;
      }
    }

    if (this.isInsideSuperset(itemId)) {
      return false;
    }

    const siblings =
      item.parentId === null
        ? this.rootWorkoutItems
        : (this.workoutItemsByParentId[item.parentId] ?? []);

    const sortedSiblings = sort(siblings).asc((x) => {
      return x.order;
    });
    const currentIndex = sortedSiblings.findIndex((s) => {
      return s.id === itemId;
    });
    const nextSibling = sortedSiblings[currentIndex + 1];

    if (!nextSibling) {
      return false;
    }

    if (nextSibling.type === "GROUP" && "groupType" in nextSibling) {
      const nextGroupType = nextSibling.groupType;

      if (nextGroupType !== null && nextGroupType !== "SUPER_SET") {
        return false;
      }
    }

    if (item.parentId !== null) {
      const parent = this.workoutItemById[item.parentId];

      if (parent && parent.type === "GROUP" && "format" in parent) {
        if (parent.format === "AMRAP" || parent.format === "TIMED") {
          return false;
        }
      }
    }

    return true;
  }

  getNextSiblingId(itemId: string): string | null {
    const item = this.workoutItemById[itemId];

    if (!item) {
      return null;
    }

    const siblings =
      item.parentId === null
        ? this.rootWorkoutItems
        : (this.workoutItemsByParentId[item.parentId] ?? []);

    const sortedSiblings = sort(siblings).asc((x) => {
      return x.order;
    });
    const currentIndex = sortedSiblings.findIndex((s) => {
      return s.id === itemId;
    });
    const nextSibling = sortedSiblings[currentIndex + 1];

    return nextSibling?.id ?? null;
  }

  canUnlink(itemId: string): boolean {
    const item = this.workoutItemById[itemId];

    if (!item || item.parentId === null) {
      return false;
    }

    const parent = this.workoutItemById[item.parentId];

    if (!parent || parent.type !== "GROUP") {
      return false;
    }

    if ("groupType" in parent && parent.groupType === "SUPER_SET") {
      return true;
    }

    return false;
  }

  getInsertOptionsForParent(parentId: string | null): InsertOptions {
    if (parentId === null) {
      return {
        canInsertExercise: true,
        canInsertSuperset: true,
        canInsertSection: true,
        availableSectionFormats: ["REGULAR", "AMRAP", "TIMED", "FREESTYLE"],
      };
    }

    const parent = this.getWorkoutItemById(parentId);

    if (parent.type !== "GROUP") {
      return {
        canInsertExercise: false,
        canInsertSuperset: false,
        canInsertSection: false,
        availableSectionFormats: [],
      };
    }

    if (parent.groupType === "SUPER_SET") {
      return {
        canInsertExercise: true,
        canInsertSuperset: false,
        canInsertSection: false,
        availableSectionFormats: [],
      };
    }

    const format = parent.format;

    if (format === "AMRAP" || format === "TIMED") {
      return {
        canInsertExercise: true,
        canInsertSuperset: false,
        canInsertSection: false,
        availableSectionFormats: [],
      };
    }

    return {
      canInsertExercise: true,
      canInsertSuperset: true,
      canInsertSection: false,
      availableSectionFormats: [],
    };
  }

  canChangeFormatTo(sectionId: string, format: WorkoutFormat): boolean {
    if (format !== "AMRAP" && format !== "TIMED") {
      return true;
    }

    const children = this.workoutItemsByParentId[sectionId] ?? [];
    return children.every((child) => {
      return child.type !== "NORMAL" || child.workoutSets.length <= 1;
    });
  }

  getChildrenOf(parentId: string): WorkoutItemData[] {
    return this.workoutItemsByParentId[parentId] ?? [];
  }

  isInsideSuperset(workoutItemId: string): boolean {
    const workoutItem = this.getWorkoutItemById(workoutItemId);

    if (!workoutItem.parentId) {
      return false;
    }

    const parent = this.getWorkoutItemById(workoutItem.parentId);
    return parent.type === "GROUP" && parent.groupType === "SUPER_SET";
  }

  isInsideFreestyle(workoutItemId: string): boolean {
    let currentParentId = this.getWorkoutItemById(workoutItemId).parentId;

    while (currentParentId) {
      const parent = this.getWorkoutItemById(currentParentId);

      if (parent.type === "GROUP" && parent.format === "FREESTYLE") {
        return true;
      }

      currentParentId = parent.parentId;
    }

    return false;
  }
}

export interface InsertOptions {
  canInsertExercise: boolean;
  canInsertSuperset: boolean;
  canInsertSection: boolean;
  availableSectionFormats: WorkoutFormat[];
}
